import React, { useState, useMemo, useEffect, useRef } from 'react';
import { Layout, Menu, Grid, Spin, Badge } from 'antd';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { usePermissions } from '../contexts/PermissionsContext';
import { useSidebar } from '../contexts/SidebarContext';
import { useNotification } from '../contexts/NotificationContext';
import { useSocket } from '../hooks/useSocket';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { 
  LogoutOutlined, 
  MenuFoldOutlined, 
  MenuUnfoldOutlined, 
  MenuOutlined,
  IdcardOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  CheckSquareOutlined,
  CloseCircleOutlined,
  ApartmentOutlined,
  CalendarOutlined,
  TrophyOutlined,
  CrownOutlined,
  ToolOutlined,
  ScheduleOutlined,
  HomeOutlined,
  UserOutlined,
  TeamOutlined,
  DollarOutlined,
  SettingOutlined,
  BellOutlined,
  BarChartOutlined,
  DashboardOutlined,
  PlusOutlined,
  ThunderboltOutlined,
  CreditCardOutlined,
  PercentageOutlined,
  FileTextOutlined,
  UnorderedListOutlined,
  FormOutlined,
  MessageOutlined,
  FolderOutlined,
  FolderOpenOutlined,
  AppstoreOutlined,
  SafetyCertificateOutlined,
  BankOutlined,
  SnippetsOutlined,
  QuestionCircleOutlined,
  CustomerServiceOutlined,
  GlobalOutlined,
  TranslationOutlined,
  ProjectOutlined,
  LoginOutlined,
  PhoneOutlined,
  StopOutlined,
  InboxOutlined,
  AuditOutlined,
  FieldTimeOutlined,
  EnvironmentOutlined,
  SwapOutlined,
  WalletOutlined,
  LineChartOutlined,
  HistoryOutlined,
  PlusCircleOutlined,
  FundOutlined,
  AimOutlined,
  PayCircleOutlined,
  RollbackOutlined,
  TagsOutlined,
} from '@ant-design/icons';
import ThemeToggle from '../components/ThemeToggle';
import NavMenuLink from '../components/common/NavMenuLink';
import HeaderMyGoalWidget from '../components/leads/reports/HeaderMyGoalWidget';
import WorkflowPendingBadge from '../components/workflow/WorkflowPendingBadge';
import ClockInOutModal from '../components/attendance/ClockInOutModal';
import { useAttendance } from '../contexts/AttendanceContext';
import { resolveAvatarSrc } from '../utils/avatarUtils';
import { translateRoleName } from '../utils/formHelpers';
import { isLoginSubmenuAllowed } from '../utils/loginModuleMenus';

const { Header, Content, Sider } = Layout;
const { useBreakpoint } = Grid;

const hrIcons = {
  '/hr/dashboard': <DashboardOutlined />,
  '/hr/employees': <IdcardOutlined />,
  '/hr/leaves': <CalendarOutlined />,
  '/workflow/inbox': <InboxOutlined />,
  '/hr/shifts': <ClockCircleOutlined />,
  '/hr/attendance': <CheckCircleOutlined />,
  '/hr/attendance-regularizations': <FormOutlined />,
  '/hr/attendance-adjustments': <AuditOutlined />,
  '/hr/overtime-requests': <FieldTimeOutlined />,
  '/hr/attendance-missions': <EnvironmentOutlined />,
  '/hr/holidays': <CloseCircleOutlined />,
  '/hr/departments': <ApartmentOutlined />,
  '/hr/positions': <CrownOutlined />,
  '/hr/leaves-settings': <ToolOutlined />,
  '/hr/attendance-settings': <ScheduleOutlined />,
  '/hr/appreciations': <TrophyOutlined />,
};

const leadsIcons = {
  '/leads': <UserOutlined />,
  '/leads/deals': <DollarOutlined />,
  '/leads/lead-forms': <FormOutlined />,
  '/leads/reports': <BarChartOutlined />,
  '/leads/settings': <SettingOutlined />,
};

const clientsIcons = {
  '/clients': <TeamOutlined />,
  '/clients/add': <PlusOutlined />,
};

const financeIcons = {
  '/finance/dashboard': <DashboardOutlined />,
  '/finance/approvals': <CheckSquareOutlined />,
  '/finance/transactions': <SwapOutlined />,
  '/finance/cheques': <WalletOutlined />,
  '/finance/receivables-summary': <BarChartOutlined />,
  '/finance/aging-report': <LineChartOutlined />,
  '/finance/audit-logs': <HistoryOutlined />,
  '/finance/register-payment': <PlusCircleOutlined />,
  '/finance/bank-accounts': <DollarOutlined />,
  '/finance/petty-cash': <FundOutlined />,
  '/finance/expenses': <CreditCardOutlined />,
  '/finance/recurring-expenses': <ClockCircleOutlined />,
  '/finance/targets': <AimOutlined />,
  '/finance/commission-plans': <PercentageOutlined />,
  '/finance/payroll': <PayCircleOutlined />,
  '/finance/estimates': <FileTextOutlined />,
  '/finance/invoices': <FileTextOutlined />,
  '/finance/payment-gateway': <CreditCardOutlined />,
  '/finance/tax': <PercentageOutlined />,
  '/finance/invoice-settings': <SettingOutlined />,
  '/finance/refunds': <RollbackOutlined />,
  '/finance/discount-limits': <TagsOutlined />,
};

const ticketsIcons = {
  '/tickets': <UnorderedListOutlined />,
  '/tickets/add': <PlusOutlined />,
  '/tickets/settings': <SettingOutlined />,
  '/tickets/ticket-form': <FormOutlined />,
};

const callCenterIcons = {
  '/call-center/calls': <UnorderedListOutlined />,
  '/call-center/agents': <TeamOutlined />,
  '/call-center/settings': <SettingOutlined />,
  '/call-center/blacklist': <StopOutlined />,
  '/call-center/reports': <BarChartOutlined />,
};

const noticesIcons = {
  '/notices': <BellOutlined />,
  '/notices/add': <PlusOutlined />,
};

const messagesIcons = {
  '/messages': <MessageOutlined />,
};

const actionIcons = {
  '/action/dashboard': <DashboardOutlined />,
  '/action/my-actions': <UserOutlined />,
  '/action/create': <PlusOutlined />,
  '/action/notifications': <BellOutlined />,
  '/action/notifications/settings': <SettingOutlined />,
  '/action/reports': <BarChartOutlined />,
};

const dashboardIcons = {
  '/dashboard/private': <DashboardOutlined />,
  '/dashboard/advanced': <BarChartOutlined />,
};

const settingsIcons = {
  '/settings/company': <SettingOutlined />,
  '/settings/business-address': <HomeOutlined />,
  '/settings/currency-settings': <DollarOutlined />,
  '/settings/profile-settings': <UserOutlined />,
  '/settings/app-settings': <AppstoreOutlined />,
  '/settings/role-permissions': <SafetyCertificateOutlined />,
  '/settings/packages': <AppstoreOutlined />,
  '/settings/companies': <BankOutlined />,
  '/settings/superadmin-invoices': <SnippetsOutlined />,
  '/settings/superadmin-faqs': <QuestionCircleOutlined />,
  '/settings/superadmin-users': <CrownOutlined />,
  '/settings/support-tickets': <CustomerServiceOutlined />,
  '/settings/support-tickets/create': <PlusOutlined />,
  '/settings/superadmin/app-settings': <AppstoreOutlined />,
  '/settings/superadmin/profile': <UserOutlined />,
  '/settings/superadmin/invoice': <SnippetsOutlined />,
  '/settings/superadmin/language': <TranslationOutlined />,
  '/settings/superadmin/currency': <DollarOutlined />,
};

const reportsIcons = {
  '/reports': <AppstoreOutlined />,
  '/reports/finance': <BarChartOutlined />,
  '/reports/income-expense': <BarChartOutlined />,
  '/reports/leaves': <CalendarOutlined />,
  '/reports/leave-quota': <CalendarOutlined />,
  '/reports/attendance': <CheckCircleOutlined />,
  '/reports/expenses': <DollarOutlined />,
  '/reports/leads': <BarChartOutlined />,
  '/reports/sales': <FileTextOutlined />,
};

const workIcons = {
  '/work/tasks': <CheckSquareOutlined />,
  '/work/projects': <ProjectOutlined />,
  '/work/projects/templates': <SnippetsOutlined />,
  '/work/projects/settings': <SettingOutlined />,
  '/work/contracts': <FileTextOutlined />,
  '/work/contracts/templates': <FormOutlined />,
  '/work/contracts/settings': <SettingOutlined />,
};

const MainLayout = ({ children }) => {
  const { isDarkMode } = useTheme();
  const { logout, user } = useAuth();
  const { hasPermission, isSuperAdmin, isAdmin } = usePermissions();
  const { activeMenu, closeSidebar } = useSidebar();
  const { unreadCount } = useNotification();
  const { isConnected } = useSocket();
  const navigate = useNavigate();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(false);
  const [logoutLoading, setLogoutLoading] = useState(false);
  const [clockModalOpen, setClockModalOpen] = useState(false);
  const { isClockedIn, refreshAttendanceStatus } = useAttendance();
  const screens = useBreakpoint();
  const isMobile = !screens.lg;
  const sidebarScrollRef = useRef(null);

  const handleClockModalClose = () => {
    setClockModalOpen(false);
    refreshAttendanceStatus();
  };


  const handleLogout = async () => {
    setLogoutLoading(true);
    try {
      await logout();
      navigate('/login');
    } finally {
      setLogoutLoading(false);
    }
  };

  // فیلتر کردن آیتم‌های سایدبار بر اساس پرمیشن‌ها و modules لاگین
  const filteredMenuItems = useMemo(() => {
    if (!activeMenu?.items) return [];

    if (isSuperAdmin || isAdmin) return activeMenu.items;

    const moduleMenus = user?.moduleMenus;
    const usesLoginMenus =
      moduleMenus && typeof moduleMenus === 'object' && !Array.isArray(moduleMenus);

    return activeMenu.items.filter((item) => {
      if (usesLoginMenus) {
        return isLoginSubmenuAllowed(activeMenu.key, item.path, moduleMenus);
      }
      if (item.permission && !hasPermission(item.permission)) return false;
      return true;
    });
  }, [activeMenu?.items, activeMenu?.key, hasPermission, isSuperAdmin, isAdmin, user?.moduleMenus]);

  // پیدا کردن دقیق‌ترین match برای pathname
  const findActivePath = () => {
    const paths = filteredMenuItems
      .map((item, index) => ({ path: item.path, index }))
      .filter(item => item.path)
      .sort((a, b) => b.path.length - a.path.length); // مرتب‌سازی بر اساس طول (بلند به کوتاه)
    
    for (const { path, index } of paths) {
      if (location.pathname === path || location.pathname.startsWith(path + '/')) {
        return { path, index };
      }
    }
    return null;
  };

  const activePathInfo = findActivePath();

  const handleSidebarNavigate = () => {
    if (isMobile) setCollapsed(true);
  };

  const sidebarMenuItems = filteredMenuItems.map((item, index) => {
    const itemPath = item.path;
    const isActive = activePathInfo && activePathInfo.index === index;
    let icon = item.icon;
    if (itemPath) {
      if (hrIcons[itemPath]) {
        icon = hrIcons[itemPath];
      } else if (leadsIcons[itemPath]) {
        icon = leadsIcons[itemPath];
      } else if (clientsIcons[itemPath]) {
        icon = clientsIcons[itemPath];
      } else if (financeIcons[itemPath]) {
        icon = financeIcons[itemPath];
      } else if (ticketsIcons[itemPath]) {
        icon = ticketsIcons[itemPath];
      } else if (callCenterIcons[itemPath]) {
        icon = callCenterIcons[itemPath];
      } else if (noticesIcons[itemPath]) {
        icon = noticesIcons[itemPath];
      } else if (messagesIcons[itemPath]) {
        icon = messagesIcons[itemPath];
      } else if (settingsIcons[itemPath]) {
        icon = settingsIcons[itemPath];
      } else if (reportsIcons[itemPath]) {
        icon = reportsIcons[itemPath];
      } else if (actionIcons[itemPath]) {
        icon = actionIcons[itemPath];
      } else if (dashboardIcons[itemPath]) {
        icon = dashboardIcons[itemPath];
      } else if (workIcons[itemPath]) {
        icon = workIcons[itemPath];
      }
    }
    
    // نمایش badge برای تب اعلان‌ها
    let label = item.label;
    if (itemPath === '/action/notifications' && unreadCount > 0) {
      label = (
        <span className="flex items-center justify-between w-full">
          <span>{item.label}</span>
          <Badge
            count={unreadCount}
            style={{ backgroundColor: '#ef4444' }}
            className="ml-2"
          />
        </span>
      );
    }
    
    const linkLabel = itemPath ? (
      <NavMenuLink
        to={itemPath}
        onNavigate={handleSidebarNavigate}
        title="برای باز کردن در تب جدید: Ctrl+Click"
      >
        {label}
      </NavMenuLink>
    ) : (
      label
    );

    return {
      key: `menu-${index}`,
      icon: icon,
      label: linkLabel,
      onClick: itemPath
        ? undefined
        : () => {
            if (item.onClick) {
              handleSidebarNavigate();
              item.onClick();
            }
          },
      className: isActive ? 'ant-menu-item-selected' : '',
    };
  });

  const selectedKeys = activePathInfo ? [`menu-${activePathInfo.index}`] : [];

  // آیتم فعال را داخل ناحیه اسکرول سایدبار نمایان نگه دار
  useEffect(() => {
    if (!selectedKeys.length || !sidebarScrollRef.current) return;
    const timer = window.setTimeout(() => {
      const el = sidebarScrollRef.current?.querySelector('.ant-menu-item-selected');
      if (el?.scrollIntoView) {
        el.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      }
    }, 50);
    return () => window.clearTimeout(timer);
  }, [location.pathname, selectedKeys.join('|'), collapsed, activeMenu?.key]);

  return (
    <>
      <style jsx global>{`
        /* تنظیمات اصلی صفحه - بدون overflow hidden */
        html, body, #root {
          width: 100%;
          height: 100%;
          margin: 0;
          padding: 0;
          overflow: hidden;
        }

        /* Layout اصلی */
        .main-layout-wrapper {
          width: 100%;
          height: 100dvh;
          min-height: 100dvh;
          overflow: hidden;
          display: flex;
          flex-direction: column;
        }

        .main-content-wrapper {
          flex: 1;
          overflow: hidden;
          display: flex;
          min-height: 0;
        }

        .main-content-scrollable {
          flex: 1;
          overflow-y: auto;
          overflow-x: hidden;
          min-height: 0;
          -webkit-overflow-scrolling: touch;
        }

        /* استایل‌های سایدبار */
        .sidebar-container.ant-layout-sider {
          height: 100% !important;
          max-height: 100%;
          align-self: stretch;
          overflow: hidden !important;
        }

        .sidebar-container .ant-layout-sider-children {
          height: 100% !important;
          max-height: 100%;
          min-height: 0;
          display: flex !important;
          flex-direction: column;
          overflow: hidden;
        }

        .sidebar-container .sidebar-header {
          flex-shrink: 0;
        }

        .sidebar-container .sidebar-menu-scroll {
          flex: 1 1 auto;
          min-height: 0;
          overflow-x: hidden;
          overflow-y: auto;
          -webkit-overflow-scrolling: touch;
          overscroll-behavior: contain;
          padding-bottom: 16px;
        }

        .sidebar-container .ant-menu {
          background: transparent !important;
        }

        .sidebar-menu .ant-menu-item {
          margin: 4px 8px !important;
          border-radius: 8px !important;
          height: 48px !important;
          line-height: 48px !important;
          transition: all 0.2s ease !important;
        }
        
        .sidebar-menu .ant-menu-item:hover,
        .sidebar-menu .ant-menu-submenu-title:hover {
          background: rgba(13, 136, 126, 0.1) !important;
          color: #0D887E !important;
        }
        
        .dark .sidebar-menu .ant-menu-item:hover,
        .dark .sidebar-menu .ant-menu-submenu-title:hover {
          background: rgba(13, 136, 126, 0.2) !important;
          color: #0D887E !important;
        }
        
        .sidebar-menu .ant-menu-item-selected {
          background: rgba(13, 136, 126, 0.14) !important;
          color: #0D887E !important;
          font-weight: 600 !important;
          border-right: 3px solid #0D887E !important;
        }
        
        .dark .sidebar-menu .ant-menu-item-selected {
          background: rgba(13, 136, 126, 0.28) !important;
          color: #0D887E !important;
          border-right-color: #0D887E !important;
        }

        .dark .sidebar-menu .ant-menu-submenu-selected > .ant-menu-submenu-title {
          color: #0D887E !important;
        }

        /* اسکرول شیک — عمومی + سایدبار */
        .custom-scrollbar {
          scrollbar-width: thin;
          scrollbar-color: rgba(156, 163, 175, 0.5) transparent;
        }

        .custom-scrollbar::-webkit-scrollbar {
          width: 6px;
          height: 6px;
        }

        .custom-scrollbar::-webkit-scrollbar-track {
          background: transparent;
          border-radius: 10px;
        }

        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: rgba(156, 163, 175, 0.45);
          border-radius: 10px;
          transition: background 0.2s ease;
        }

        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: rgba(13, 136, 126, 0.55);
        }

        .dark .custom-scrollbar {
          scrollbar-color: rgba(13, 136, 126, 0.4) transparent;
        }

        .dark .custom-scrollbar::-webkit-scrollbar-thumb {
          background: rgba(13, 136, 126, 0.35);
        }

        .dark .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: rgba(13, 136, 126, 0.55);
        }

        /* اسکرول سایدبار باریک‌تر و همیشه قابل‌دیدن هنگام hover */
        .sidebar-menu-scroll.custom-scrollbar {
          scrollbar-gutter: stable;
        }

        .sidebar-menu-scroll.custom-scrollbar::-webkit-scrollbar {
          width: 5px;
        }

        /* اسکرول کانبان */
        .kanban-scrollbar {
          scrollbar-width: thin;
          scrollbar-color: rgba(59, 130, 246, 0.6) rgba(59, 130, 246, 0.1);
        }

        .kanban-scrollbar::-webkit-scrollbar {
          height: 12px;
        }

        .kanban-scrollbar::-webkit-scrollbar-track {
          background: rgba(0, 0, 0, 0.05);
          border-radius: 10px;
          margin: 8px;
        }

        .kanban-scrollbar::-webkit-scrollbar-thumb {
          background: linear-gradient(90deg, rgba(59, 130, 246, 0.6), rgba(59, 130, 246, 0.8));
          border-radius: 10px;
        }

        .dark .kanban-scrollbar {
          scrollbar-color: rgba(13, 136, 126, 0.45) rgba(13, 136, 126, 0.08);
        }

        .dark .kanban-scrollbar::-webkit-scrollbar-thumb {
          background: rgba(13, 136, 126, 0.45);
        }

        /* دکمه داشبورد شناور */
        @keyframes dashboard-fab-pulse {
          0%, 100% {
            box-shadow: 0 0 0 0 rgba(13, 136, 126, 0.55);
          }
          50% {
            box-shadow: 0 0 0 8px rgba(13, 136, 126, 0);
          }
        }
        
        .dashboard-fab {
          animation: dashboard-fab-pulse 2s ease-in-out infinite;
        }
        
        .dashboard-fab:hover {
          animation: none;
          box-shadow: 0 10px 25px rgba(13, 136, 126, 0.35);
        }

        /* سایدبار ریسپانسیو - موبایل */
        @media (max-width: 991px) {
          .sidebar-mobile.ant-layout-sider {
            height: calc(100dvh - 64px) !important;
            max-height: calc(100dvh - 64px) !important;
          }
          .sidebar-mobile.ant-layout-sider-collapsed {
            width: 0 !important;
            min-width: 0 !important;
            max-width: 0 !important;
            overflow: hidden !important;
          }
          .sidebar-mobile .ant-layout-sider-children {
            width: 280px;
            height: 100% !important;
          }
        }
      `}</style>

      <div className="main-layout-wrapper">
        {/* هدر ثابت */}
        <Header 
          className={`flex items-center justify-between px-4 md:px-6 h-16 border-b shadow-sm ${
            isDarkMode ? 'border-[rgba(13,136,126,0.22)] panel-dark-chrome' : 'border-gray-200'
          }`}
          style={{ 
            position: 'sticky',
            top: 0,
            zIndex: 1000,
            width: '100%',
            flexShrink: 0,
            ...(isDarkMode ? {} : { background: 'linear-gradient(135deg, #ffffff 0%, #f0faf9 50%, #ffffff 100%)' }),
          }}
        >
          <div className="flex items-center gap-3 md:gap-4 flex-shrink-0">
            {activeMenu && (
              <button
                onClick={() => setCollapsed(!collapsed)}
                className={`
                  flex items-center justify-center w-10 h-10 rounded-lg
                  transition-all duration-200
                  ${isDarkMode
                    ? 'text-white/70 hover:bg-[rgba(13,136,126,0.12)] hover:text-brand-primary'
                    : 'text-gray-600 hover:bg-gray-100'
                  }
                  ${isMobile ? '' : 'hidden'}
                `}
                title="منو"
              >
                <MenuOutlined className="text-lg" />
              </button>
            )}

            <div
              className={`w-8 h-8 md:w-10 md:h-10 rounded-full ring-2 flex-shrink-0 flex items-center justify-center bg-brand-primary ${
                isDarkMode ? 'ring-[rgba(13,136,126,0.35)]' : 'ring-gray-200'
              }`}
              aria-hidden
            >
              <span className="text-white text-xs md:text-sm font-bold" style={{ fontFamily: 'CustomFont' }}>
                ERP
              </span>
            </div>
            <h1 className="text-lg md:text-xl font-bold m-0 dark:text-white hidden sm:block" style={{ fontFamily: 'CustomFont' }}>
              ERP Panel
            </h1>
            
            <Link
              to="/dashboard"
              onClick={() => closeSidebar()}
              className={`
                flex items-center justify-center w-10 h-10 rounded-lg
                transition-all duration-200 group relative
                ${isDarkMode 
                  ? 'text-white/70 hover:bg-[rgba(13,136,126,0.12)] hover:text-brand-primary' 
                  : 'text-gray-600 hover:bg-blue-50 hover:text-blue-600'
                }
              `}
              title="بازگشت به داشبورد (Ctrl+Click برای تب جدید)"
            >
              <HomeOutlined className="text-lg" />
            </Link>
          </div>
          
          {/* باکس جستجو موقتاً غیرفعال شده طبق درخواست */}
          {/* <div className="flex-1 flex justify-center mx-4 max-w-md">
            <div
              className={`
                relative flex items-center w-full rounded-lg h-9
                transition-all duration-200
                ${isDarkMode 
                  ? 'bg-gray-700/50 border border-gray-600 focus-within:border-blue-500' 
                  : 'bg-gray-50 border border-gray-200 focus-within:border-blue-500 focus-within:shadow-md'
                }
              `}
            >
              <SearchOutlined
                className={`absolute right-2.5 text-base ${
                  isDarkMode ? 'text-gray-400' : 'text-gray-500'
                }`}
              />
              <input
                type="text"
                placeholder="جستجو در پنل..."
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    handleSearch(searchValue);
                  }
                }}
                className={`
                  w-full pr-9 pl-3 py-1.5 text-sm rounded-lg outline-none
                  ${isDarkMode 
                    ? 'bg-transparent text-white placeholder-gray-400' 
                    : 'bg-transparent text-gray-800 placeholder-gray-400'
                  }
                `}
                style={{ fontFamily: 'CustomFont' }}
              />
              {searchValue && (
                <button
                  onClick={() => setSearchValue('')}
                  className={`
                    absolute left-2.5 w-4 h-4 rounded-full
                    flex items-center justify-center text-xs
                    ${isDarkMode 
                      ? 'text-gray-400 hover:bg-gray-600 hover:text-white' 
                      : 'text-gray-400 hover:bg-gray-200 hover:text-gray-600'
                    }
                  `}
                >
                  ✕
                </button>
              )}
            </div>
          </div> */}
          
          <div className="flex items-center gap-2 md:gap-3 flex-shrink-0">
            <button
              type="button"
              onClick={() => setClockModalOpen(true)}
              className={`
                relative flex items-center justify-center gap-1.5 h-10 px-3.5 rounded-lg text-sm font-semibold
                transition-all duration-300 shadow-sm
                ${isClockedIn
                  ? isDarkMode
                    ? 'text-amber-200 bg-amber-950/40 border border-amber-700/60 hover:bg-amber-900/50 hover:border-amber-500/70'
                    : 'text-amber-800 bg-amber-50 border border-amber-300 hover:bg-amber-100 hover:border-amber-400'
                  : isDarkMode
                    ? 'text-emerald-200 bg-emerald-950/35 border border-emerald-700/55 hover:bg-emerald-900/45 hover:border-emerald-500/65'
                    : 'text-emerald-700 bg-emerald-50 border border-emerald-300 hover:bg-emerald-100 hover:border-emerald-400'
                }
              `}
              title={isClockedIn ? 'ثبت خروج' : 'ثبت ورود'}
              style={{ fontFamily: 'CustomFont' }}
            >
              <span
                className={`absolute top-1.5 right-1.5 w-2 h-2 rounded-full ${
                  isClockedIn ? 'bg-amber-400 animate-pulse' : 'bg-emerald-400'
                }`}
                aria-hidden
              />
              {isClockedIn ? (
                <LogoutOutlined className="text-base" />
              ) : (
                <LoginOutlined className="text-base" />
              )}
              <span className="hidden sm:inline">{isClockedIn ? 'خروج' : 'ورود'}</span>
            </button>
            <HeaderMyGoalWidget isDarkMode={isDarkMode} />
            <WorkflowPendingBadge isDarkMode={isDarkMode} />
          <ThemeToggle />
            
            <button
              onClick={handleLogout}
              disabled={logoutLoading}
              className={`
                flex items-center justify-center w-10 h-10 rounded-lg
                transition-all duration-200 group
                ${isDarkMode 
                  ? 'text-gray-300 hover:bg-red-900/30 hover:text-red-400' 
                  : 'text-gray-600 hover:bg-red-50 hover:text-red-600'
                }
              `}
              title="خروج"
            >
              <LogoutOutlined className="text-lg" />
            </button>

            {/* وضعیت اتصال سوکت در حالت موبایل */}
            <div className="flex md:hidden items-center">
              <div className="flex items-center justify-center p-1.5 rounded-lg bg-black/5 dark:bg-white/5">
                <span
                  className={`w-2.5 h-2.5 rounded-full border border-white dark:border-gray-800 transition-all duration-300 ${
                    isConnected
                      ? 'bg-emerald-500 shadow-[0_0_6px_rgba(16,185,129,0.8)]'
                      : 'bg-rose-500 shadow-[0_0_6px_rgba(244,63,94,0.8)]'
                  }`}
                />
              </div>
            </div>

            {user && (() => {
              const userAvatarSrc = resolveAvatarSrc(user.image_url);
              return (
              <div className={`
                hidden md:flex items-center gap-2 px-3 py-1.5 rounded-lg cursor-default
                ${isDarkMode 
                  ? 'bg-[rgba(13,136,126,0.08)] hover:bg-[rgba(13,136,126,0.14)] border border-[rgba(13,136,126,0.22)]' 
                  : 'bg-gray-50 hover:bg-gray-100 border border-gray-200'
                }
              `}>
                <div className="relative flex-shrink-0">
                  {userAvatarSrc ? (
                    <img
                      src={userAvatarSrc}
                      alt={user.name || 'کاربر'}
                      className={`w-9 h-9 rounded-full object-cover ring-2 ${
                        isDarkMode ? 'ring-[rgba(13,136,126,0.35)]' : 'ring-blue-500/20'
                      }`}
                      onError={(e) => {
                        e.target.style.display = 'none';
                        const fallback = e.target.parentElement.querySelector('.avatar-fallback');
                        if (fallback) fallback.style.display = 'flex';
                      }}
                    />
                  ) : null}
                  <div
                    className={`
                      avatar-fallback w-9 h-9 rounded-full flex items-center justify-center
                      ${isDarkMode ? 'bg-[rgba(13,136,126,0.2)] text-brand-primary' : 'bg-blue-100 text-blue-600'}
                    `}
                    style={{
                      display: userAvatarSrc ? 'none' : 'flex',
                      position: userAvatarSrc ? 'absolute' : 'relative',
                      top: userAvatarSrc ? 0 : undefined,
                      left: userAvatarSrc ? 0 : undefined,
                    }}
                  >
                    <UserOutlined className="text-base" />
                  </div>

                  {/* دایره کوچک وضعیت اتصال سوکت روی آواتار کاربر */}
                  <span
                    className={`
                      absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full border-2 transition-all duration-300
                      ${isDarkMode ? 'border-gray-900' : 'border-white'}
                      ${
                        isConnected
                          ? 'bg-emerald-500 shadow-[0_0_6px_rgba(16,185,129,0.8)]'
                          : 'bg-rose-500 shadow-[0_0_6px_rgba(244,63,94,0.8)]'
                      }
                    `}
                  />
                </div>
                
                <div className="flex flex-col items-start min-w-0">
                  <span 
                    className={`text-sm font-semibold truncate max-w-[150px] ${isDarkMode ? 'text-white' : 'text-gray-800'}`}
                    style={{ fontFamily: 'CustomFont' }}
                    title={user.name}
                  >
                    {user.name}
                  </span>
                  {user.roles && user.roles.length > 0 && (
                    <span 
                      className={`text-xs truncate max-w-[150px] ${isDarkMode ? 'text-white/65' : 'text-gray-500'}`}
                      style={{ fontFamily: 'CustomFont' }}
                    >
                      {translateRoleName(user.roles[0].display_name || user.roles[0].name)}
                    </span>
                  )}
                </div>
              </div>
              );
            })()}

           
          </div>
        </Header>

        {/* محتوا با سایدبار */}
        <div className="main-content-wrapper">
          {activeMenu && (
            <Sider
              width={280}
              collapsible
              collapsed={collapsed}
              onCollapse={setCollapsed}
              trigger={null}
              breakpoint="lg"
              collapsedWidth={isMobile ? 0 : 80}
              onBreakpoint={(broken) => {
                if (broken) setCollapsed(true);
              }}
              className={`sidebar-container ${isMobile ? 'sidebar-mobile' : ''} ${isDarkMode ? 'panel-dark-chrome-vertical' : ''}`}
              theme={isDarkMode ? 'dark' : 'light'}
              style={{
                ...(isDarkMode ? {} : { background: 'linear-gradient(180deg, #ffffff 0%, #f3fbf9 55%, #ffffff 100%)' }),
                boxShadow: isDarkMode 
                  ? '2px 0 12px rgba(0,0,0,0.4)' 
                  : '2px 0 12px rgba(0,0,0,0.15)',
                overflow: 'hidden',
                height: isMobile ? 'calc(100dvh - 64px)' : '100%',
                maxHeight: isMobile ? 'calc(100dvh - 64px)' : '100%',
                position: isMobile ? 'fixed' : 'relative',
                top: isMobile ? 64 : undefined,
                right: isMobile ? 0 : undefined,
                left: isMobile ? 'auto' : undefined,
                zIndex: isMobile ? 1100 : undefined,
                transition: 'transform 0.3s ease, width 0.3s ease',
                ...(isMobile && collapsed ? { width: 0, minWidth: 0, maxWidth: 0, overflow: 'hidden' } : {}),
              }}
            >
              <div
                className={`
                  sidebar-header p-5 border-b 
                  ${isDarkMode ? 'border-[rgba(13,136,126,0.22)] bg-transparent' : 'border-gray-200'}
                `}
              >
                <div className="flex items-center justify-between">
                  {!collapsed && (
                    <div className="flex items-center gap-3">
                      <div className={`
                        w-10 h-10 rounded-lg flex items-center justify-center
                        ${isDarkMode ? 'bg-[rgba(13,136,126,0.2)] text-brand-primary' : 'bg-[#0D887E]/10 text-[#0D887E]'}
                      `}>
                        {activeMenu.key === 'hr' && <IdcardOutlined className="text-lg" />}
                        {activeMenu.key === 'leads' && <UserOutlined className="text-lg" />}
                        {activeMenu.key === 'reports' && <BarChartOutlined className="text-lg" />}
                        {activeMenu.key === 'work' && <ProjectOutlined className="text-lg" />}
                        {activeMenu.key === 'superadmin-panel' && <CrownOutlined className="text-lg" />}
                        {activeMenu.key === 'superadmin-dashboard' && <DashboardOutlined className="text-lg" />}
                        {activeMenu.key === 'superadmin-settings' && <SettingOutlined className="text-lg" />}
                        {activeMenu.key === 'settings' && <SettingOutlined className="text-lg" />}
                        {activeMenu.key === 'workflow' && <InboxOutlined className="text-lg" />}
                        {activeMenu.key === 'finance' && <DollarOutlined className="text-lg" />}
                      </div>
                      <h3 className={`text-lg font-bold m-0 ${isDarkMode ? 'text-white' : 'text-gray-800'}`} style={{ fontFamily: 'CustomFont' }}>
                        {activeMenu.label}
                      </h3>
                    </div>
                  )}
                  <button
                    type="button"
                    onClick={() => setCollapsed(!collapsed)}
                    className={`
                      flex items-center justify-center w-9 h-9 rounded-lg
                      ${isDarkMode 
                        ? 'text-white/70 hover:bg-[rgba(13,136,126,0.12)] hover:text-brand-primary' 
                        : 'text-gray-600 hover:bg-gray-100'
                      }
                    `}
                  >
                    {collapsed ? <MenuUnfoldOutlined className="text-lg" /> : <MenuFoldOutlined className="text-lg" />}
                  </button>
                </div>
              </div>

              <div ref={sidebarScrollRef} className="sidebar-menu-scroll custom-scrollbar">
                <Menu
                  mode="inline"
                  selectedKeys={selectedKeys}
                  items={sidebarMenuItems}
                  className={`border-0 sidebar-menu ${isDarkMode ? 'bg-transparent' : 'bg-white'}`}
                  style={{ fontFamily: 'CustomFont', backgroundColor: 'transparent' }}
                />
              </div>
            </Sider>
          )}

          {/* Overlay برای بستن سایدبار در موبایل */}
          {activeMenu && isMobile && !collapsed && (
            <div
              onClick={() => setCollapsed(true)}
              className="fixed inset-0"
              style={{
                background: 'rgba(0,0,0,0.35)',
                zIndex: 1050,
              }}
            />
          )}

          <div className={`main-content-scrollable custom-scrollbar ${isDarkMode ? 'panel-dark-bg' : 'panel-light-bg'}`}>
            <div
              className="px-4 md:px-6 py-6"
              style={{
                minHeight: '100%',
                paddingBottom: 'calc(1.5rem + env(safe-area-inset-bottom, 0px))',
              }}
            >
              {children}
            </div>
          </div>
        </div>

        {location.pathname !== '/dashboard' && (
          <div className="fixed bottom-6 right-6 z-50">
            <Link
              to="/dashboard"
              onClick={() => closeSidebar()}
              className={`
                w-12 h-12 rounded-full flex items-center justify-center
                shadow-2xl transition-all duration-300 group relative
                bg-brand-primary hover:bg-brand-primary-hover text-white
                dashboard-fab
              `}
              title="داشبورد (Ctrl+Click برای تب جدید)"
            >
              <HomeOutlined className="text-lg relative z-10" />
              <span className={`
                absolute -top-10 left-1/2 -translate-x-1/2
                px-2 py-1 rounded text-xs bg-brand-dark border border-[rgba(13,136,126,0.22)] text-white whitespace-nowrap
                opacity-0 group-hover:opacity-100 transition-all duration-300
                pointer-events-none shadow-xl
              `} style={{ fontFamily: 'CustomFont' }}>
                داشبورد
              </span>
            </Link>
          </div>
        )}
      </div>
      {logoutLoading ? (
        <div className="fixed inset-0 z-[2000] flex items-center justify-center bg-black/35 backdrop-blur-[1px]">
          <div className="rounded-2xl px-6 py-5 shadow-2xl dark:panel-dark-chrome dark:border dark:border-[rgba(13,136,126,0.22)] bg-white custom-font">
            <div className="flex items-center gap-3">
              <Spin size="large" />
              <span className="text-base font-semibold text-gray-800 dark:text-white">در حال خروج</span>
            </div>
          </div>
        </div>
      ) : null}
      <ClockInOutModal open={clockModalOpen} onClose={handleClockModalClose} />
    </>
  );
};

export default MainLayout;